import React, { useState } from 'react';

interface HeaderProps {
  onJoinWaitlist?: () => void;
}

const Header: React.FC<HeaderProps> = ({ onJoinWaitlist }) => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  return (
    <>
      <style>{`
        nav {
          position: fixed;
          top: 0;
          width: 100%;
          background: rgba(255,255,255,0.95);
          backdrop-filter: blur(12px);
          border-bottom: 1px solid var(--gray-200);
          z-index: 1000;
        }

        .nav-inner {
          display: flex;
          justify-content: space-between;
          align-items: center;
          height: 72px;
        }

        .logo { height: 80px; }

        .nav-menu {
          display: flex;
          align-items: center;
          gap: 32px;
        }

        .nav-menu > a, .dropdown > a, .dropdown > button {
          color: var(--gray-900);
          text-decoration: none;
          font-weight: 500;
          font-size: 15px;
          transition: color 0.2s;
          cursor: pointer;
        }

        .nav-menu > a:hover, .dropdown > a:hover, .dropdown > button:hover { color: var(--blue-600); }

        .dropdown {
          position: relative;
        }

        .dropdown-toggle {
          display: flex;
          align-items: center;
          gap: 4px;
          background: none;
          border: none;
          padding: 0;
        }

        .dropdown-toggle::after {
          content: '▼';
          font-size: 10px;
          transition: transform 0.2s;
        }

        .dropdown:hover .dropdown-toggle::after {
          transform: rotate(180deg);
        }

        .dropdown-menu {
          position: absolute;
          top: 100%;
          left: 0;
          background: var(--white);
          border: 1px solid var(--gray-200);
          border-radius: 8px;
          padding: 8px 0;
          min-width: 180px;
          margin-top: 8px;
          opacity: 0;
          visibility: hidden;
          transform: translateY(-10px);
          transition: all 0.2s;
          box-shadow: 0 10px 40px rgba(0,0,0,0.1);
        }

        .dropdown:hover .dropdown-menu {
          opacity: 1;
          visibility: visible;
          transform: translateY(0);
        }

        .dropdown-menu a {
          display: block;
          padding: 10px 20px;
          color: var(--gray-900);
          text-decoration: none;
          font-size: 14px;
          transition: background 0.2s;
        }

        .dropdown-menu a:hover {
          background: var(--gray-100);
        }

        .nav-actions {
          display: flex;
          align-items: center;
          gap: 12px;
        }

        .btn {
          display: inline-flex;
          align-items: center;
          gap: 8px;
          padding: 10px 20px;
          border-radius: 8px;
          font-weight: 600;
          font-size: 14px;
          text-decoration: none;
          transition: all 0.2s;
          border: none;
          cursor: pointer;
        }

        .btn-primary {
          background: var(--blue-600);
          color: var(--white);
        }
        .btn-primary:hover {
          background: var(--blue-700);
          transform: translateY(-1px);
        }

        .btn-outline {
          background: transparent;
          color: var(--gray-900);
          border: 1px solid var(--gray-200);
        }
        .btn-outline:hover {
          border-color: var(--blue-600);
          color: var(--blue-600);
        }

        .btn-gold {
          background: #D4A853;
          color: #0F172A;
        }
        .btn-gold:hover {
          background: #B8923A;
          transform: translateY(-1px);
        }

        .mobile-toggle {
          display: none;
          flex-direction: column;
          gap: 5px;
          background: none;
          border: none;
          cursor: pointer;
          padding: 8px;
        }
        .mobile-toggle span {
          width: 22px;
          height: 2px;
          background: var(--gray-900);
          transition: 0.2s;
        }

        .mobile-menu {
          display: none;
          position: fixed;
          top: 72px;
          left: 0;
          right: 0;
          background: var(--white);
          border-bottom: 1px solid var(--gray-200);
          padding: 16px 24px;
          max-height: calc(100vh - 72px);
          overflow-y: auto;
        }
        .mobile-menu.active { display: block; }
        .mobile-menu a {
          display: block;
          padding: 12px 0;
          color: var(--gray-900);
          text-decoration: none;
          font-weight: 500;
          border-bottom: 1px solid var(--gray-100);
        }
        .mobile-menu .mobile-section {
          font-size: 12px;
          color: var(--gray-500);
          text-transform: uppercase;
          margin-top: 16px;
          margin-bottom: 8px;
        }

        @media (max-width: 768px) {
          .nav-menu, .nav-actions { display: none; }
          .mobile-toggle { display: flex; }
        }
      `}</style>

      <nav>
        <div className="container">
          <div className="nav-inner">
            <a href="/">
              <img src="/Rewardly_logo.png" alt="Rewardly Protocol" className="logo" />
            </a>
            
            <div className="nav-menu">
              <a href="/">Home</a>
              <a href="/vision">Vision</a>
              <a href="/whitepaper">Whitepaper</a>
              <a href="/coalition">Coalition</a>

              <div className="dropdown">
                <button className="dropdown-toggle" type="button">Learn More</button>
                <div className="dropdown-menu">
                  <a href="/platform">Platform</a>
                  <a href="/innovation">Innovation</a>
                  <a href="/trust-framework">Trust Framework</a>
                  <a href="/manifesto">Manifesto</a>
                </div>
              </div>

              <div className="dropdown">
                <button className="dropdown-toggle" type="button">Community</button>
                <div className="dropdown-menu">
                  <a href="https://x.com/Rewardly_io" target="_blank" rel="noopener noreferrer">Twitter/X</a>
                  <a href="https://www.linkedin.com/company/rewardly-protocol-inc/" target="_blank" rel="noopener noreferrer">LinkedIn</a>
                  <a href="https://discord.gg/tESNkuhn6v" target="_blank" rel="noopener noreferrer">Discord</a>
                  <a href="https://t.me/REWARDLYPROTOCOL" target="_blank" rel="noopener noreferrer">Telegram</a>
                  <a href="mailto:hello@rewardly.live">Contact</a>
                </div>
              </div>
            </div>
            
            <div className="nav-actions">
              <a href="https://x.com/Rewardly_io" target="_blank" rel="noopener noreferrer" className="btn btn-gold">Follow Updates</a>
              <button onClick={onJoinWaitlist} className="btn btn-primary">Join Waitlist</button>
            </div>
            
            <button className="mobile-toggle" onClick={() => setMobileMenuOpen(!mobileMenuOpen)}>
              <span></span><span></span><span></span>
            </button>
          </div>
        </div>
        
        <div className={`mobile-menu ${mobileMenuOpen ? 'active' : ''}`}>
          <a href="/" onClick={() => setMobileMenuOpen(false)}>Home</a>
          <a href="/vision" onClick={() => setMobileMenuOpen(false)}>Vision</a>
          <a href="/whitepaper" onClick={() => setMobileMenuOpen(false)}>Whitepaper</a>
          <a href="/coalition" onClick={() => setMobileMenuOpen(false)}>Coalition</a>
          <div className="mobile-section">Learn More</div>
          <a href="/platform" onClick={() => setMobileMenuOpen(false)}>Platform</a>
          <a href="/innovation" onClick={() => setMobileMenuOpen(false)}>Innovation</a>
          <a href="/trust-framework" onClick={() => setMobileMenuOpen(false)}>Trust Framework</a>
          <a href="/manifesto" onClick={() => setMobileMenuOpen(false)}>Manifesto</a>
          <div className="mobile-section">Community</div>
          <a href="https://x.com/Rewardly_io" target="_blank" rel="noopener noreferrer">Twitter/X</a>
          <a href="https://www.linkedin.com/company/rewardly-protocol-inc/" target="_blank" rel="noopener noreferrer">LinkedIn</a>
          <a href="https://discord.gg/tESNkuhn6v" target="_blank" rel="noopener noreferrer">Discord</a>
          <a href="https://t.me/REWARDLYPROTOCOL" target="_blank" rel="noopener noreferrer">Telegram</a>
          <a href="mailto:hello@rewardly.live">Contact</a>
        </div>
      </nav>
    </>
  );
};

export default Header;